import React, { Fragment, memo, useCallback, useMemo } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { Form, Formik } from 'formik'
import dayjs from 'dayjs'
import { DateTime } from 'luxon'
import { ReviewDateField } from 'src/components/document-review/review/ReviewDateField'
import { Button } from 'src/components/shared/Button'
import { CommonModal } from 'src/components/shared/CommonModal'
import type { BasicRadioOption } from 'src/components/shared/form/BasicRadioGroup'
import { FormikFileInput } from 'src/components/shared/form/formik/FormikFileInput'
import { FormikRadioGroup } from 'src/components/shared/form/formik/FormikRadioGroup'
import { FormikSelect } from 'src/components/shared/form/formik/FormikSelect'
import { FormikTextField } from 'src/components/shared/form/formik/FormikTextField'
import { MedicalCardRequirement } from 'src/constants/collect-data/enums'
import {
  DOT_STATE_OF_ISSUE_OPTIONS,
  dotNumberOptionTitles,
} from 'src/constants/partnerDriver/constants'
import { DotNumberOption } from 'src/constants/partnerDriver/enums'
import { buildCompleteDotNumberValidator } from './CompleteDotNumberForm.validator'
import type { CompleteDotNumberFormValues } from './CompleteDotNumberForm.interface'

const FORM_ID = 'partner-driver-complete-dot-number-form'

// Numeric keyboard without a mask, so a wrong value still reaches the 1–8 digits error.
const NUMBER_INPUT_PROPS = { inputMode: 'numeric' as const }

interface Props {
  isOpen: boolean
  stepLabel: string
  /** `step.optional` — adds the "Don't have a DOT/USDOT number" choice, which skips the step. */
  isOptional: boolean
  medicalCardRequirement: MedicalCardRequirement
  isSubmitting: boolean
  onSubmit(values: CompleteDotNumberFormValues): void
  onClose(): void
}

/** ENG-885 — the admin side of the client app's DOT form (`apps/client/.../DOTNumberForm.tsx`). */
const CompleteDotNumberModalComponent = ({
  isOpen,
  stepLabel,
  isOptional,
  medicalCardRequirement,
  isSubmitting,
  onSubmit,
  onClose,
}: Props) => {
  // Fixed for the modal's lifetime: the default date and the future-date check share one "today".
  const today = useMemo(() => {
    return DateTime.now().toISODate() ?? ''
  }, [])

  const initialValues = useMemo<CompleteDotNumberFormValues>(() => {
    return {
      option: DotNumberOption.Dot,
      dotNumber: '',
      stateOfIssue: '',
      usDotNumber: '',
      lastSubmittedAt: today,
      file: null,
    }
  }, [today])

  const validationSchema = useMemo(() => {
    return buildCompleteDotNumberValidator({ medicalCardRequirement, isOptional, today })
  }, [medicalCardRequirement, isOptional, today])

  const optionChoices = useMemo<BasicRadioOption[]>(() => {
    return Object.values(DotNumberOption)
      .filter((option) => {
        return isOptional || option !== DotNumberOption.NoNumber
      })
      .map((option) => {
        return { label: dotNumberOptionTitles[option], value: option }
      })
  }, [isOptional])

  const handleSubmit = useCallback(
    (values: CompleteDotNumberFormValues) => {
      onSubmit(values)
    },
    [onSubmit],
  )

  const fileLabel =
    medicalCardRequirement === MedicalCardRequirement.Required
      ? 'DOT medical card *'
      : 'DOT medical card (optional)'

  return (
    <Formik<CompleteDotNumberFormValues>
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ values }) => {
        const isSkip = values.option === DotNumberOption.NoNumber

        return (
          <CommonModal
            isOpen={isOpen}
            title={`Complete ${stepLabel}`}
            className={tw`max-w-[560px]`}
            onClose={onClose}
            buttons={
              <Fragment>
                <Button size="small" color="grey" disabled={isSubmitting} onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  size="small"
                  color="dark"
                  type="submit"
                  form={FORM_ID}
                  loading={isSubmitting}
                  disabled={isSubmitting}
                  data-testid="partner-driver-dot-number-submit-button"
                >
                  {isSkip ? 'Skip step' : 'Submit'}
                </Button>
              </Fragment>
            }
          >
            <Form id={FORM_ID} css={tw`flex flex-col gap-4`}>
              <FormikRadioGroup<CompleteDotNumberFormValues>
                name="option"
                options={optionChoices}
                dataTestId="partner-driver-dot-number-option-input"
              />
              {values.option === DotNumberOption.Dot ? (
                <div css={tw`grid grid-cols-1 gap-4 md:grid-cols-2`}>
                  <FormikTextField<CompleteDotNumberFormValues>
                    name="dotNumber"
                    label="DOT number *"
                    placeholder="1 to 8 digits"
                    inputProps={{
                      ...NUMBER_INPUT_PROPS,
                      'data-testid': 'partner-driver-dot-number-input',
                    }}
                  />
                  <FormikSelect<CompleteDotNumberFormValues>
                    name="stateOfIssue"
                    label="State of issue *"
                    options={DOT_STATE_OF_ISSUE_OPTIONS}
                    placeholder="Select state"
                  />
                </div>
              ) : null}
              {values.option === DotNumberOption.UsDot ? (
                <FormikTextField<CompleteDotNumberFormValues>
                  name="usDotNumber"
                  label="USDOT number *"
                  placeholder="1 to 8 digits"
                  inputProps={{
                    ...NUMBER_INPUT_PROPS,
                    'data-testid': 'partner-driver-us-dot-number-input',
                  }}
                />
              ) : null}
              {isSkip ? (
                <Typography variant="body2" css={tw`text-shades-500`}>
                  The step is optional, so it will be skipped.
                </Typography>
              ) : (
                <Fragment>
                  <ReviewDateField<CompleteDotNumberFormValues>
                    name="lastSubmittedAt"
                    label="Date of last submitted MCS-150 *"
                    maxDate={dayjs(today)}
                  />
                  {medicalCardRequirement === MedicalCardRequirement.Hidden ? null : (
                    <FormikFileInput<CompleteDotNumberFormValues>
                      name="file"
                      label={fileLabel}
                      inputDataTestId="partner-driver-dot-number-file-input"
                    />
                  )}
                </Fragment>
              )}
            </Form>
          </CommonModal>
        )
      }}
    </Formik>
  )
}

export const CompleteDotNumberModal = memo(CompleteDotNumberModalComponent)
